import { icons as legacyIconMap } from '@/components/elements/Icon.vue';

import type { ActionVariant } from './types';

/**
 * Filter-config `type` values DataGrid treats as a search-style filter
 * (renders as a primary input, never as a chip). Existing pages use both
 * spellings — `'search'` is the canonical one, `'query'` is legacy carried
 * over from older `useXxxFilters` composables that send `?query=` to the
 * backend. Both render the same way in the UI; the `type` value still drives
 * the backend param key.
 */
export const SEARCH_FILTER_TYPES = ['search', 'query'] as const;

export const isSearchFilter = (state: { type?: string } | null | undefined): boolean => {
  return !!state && SEARCH_FILTER_TYPES.includes(state.type as any);
};

/**
 * Translate the project's short icon names (e.g. `'edit'`) to the fully
 * qualified Iconify name UIcon understands (`'lucide:edit'`). Pass-through for
 * names that are already fully qualified or unknown — the icon library will
 * fall back to its own placeholder.
 */
export const resolveIconName = (name?: string): string | undefined => {
  if (!name) return undefined;
  // Already fully qualified — Nuxt Icon supports both `lucide:edit` and
  // `i-lucide-edit` forms, so leave them alone.
  if (name.includes(':') || name.startsWith('i-')) return name;
  return legacyIconMap[name] ?? name;
};

/**
 * UButton appearance derived from the action shape.
 *
 *   action.variant='danger'  →  color='error'
 *   action.variant='primary' →  color='primary'
 *   (none)                   →  color='neutral'
 *
 *   action.flat: true        →  variant='ghost'                  (toolbar only)
 *   action.outline: true     →  variant='outline'
 *   action.flat + outline    →  variant='soft'                   (toolbar only)
 *   none of the above        →  variant=undefined                (inherit UButton's
 *                                                                 app-config default,
 *                                                                 currently `subtle`)
 *
 * Row actions do not expose `flat` — they should read as low-weight icon
 * buttons in tight rows, so they always pick up the `subtle` default unless
 * the consumer explicitly opts into `outline`.
 */
export interface ButtonAppearanceInput {
  variant?: ActionVariant;
  flat?: boolean;
  outline?: boolean;
}

export interface ResolvedButtonAppearance {
  color: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  /** `undefined` means "let UButton use its app-config default variant". */
  variant: 'solid' | 'outline' | 'ghost' | 'soft' | 'subtle' | undefined;
}

const COLOR_MAP: Record<ActionVariant, ResolvedButtonAppearance['color']> = {
  default: 'neutral',
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  info: 'info',
};

export const resolveButtonAppearance = (
  input: ButtonAppearanceInput,
): ResolvedButtonAppearance => {
  const color = COLOR_MAP[input.variant ?? 'default'];

  let variant: ResolvedButtonAppearance['variant'];
  if (input.flat && input.outline) {
    variant = 'soft';
  } else if (input.flat) {
    variant = 'ghost';
  } else if (input.outline) {
    variant = 'outline';
  } else {
    variant = undefined; // inherit UButton's default
  }

  return { color, variant };
};
